import { css } from '@emotion/react';
import { colors } from '@/styles';
import onwardLogo from '@/assets/onwardlogotransparent.webp';
import { Grid, Checkbox, TextField, MenuItem, Tooltip } from '@material-ui/core';
import { OptionRow, Circle, SubTitle, Label } from './blocks';
import HandshakeIcon from '@mui/icons-material/Handshake';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { genOptionDisclaimer, MARKETPLACE_TIERS } from '@onward-delivery/core';

const dollar = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
});

export const MMOption = ({ override_attr, breakdown, overridesById, order, callbacks, ...rest }) => {
    const isSelected =
        order[override_attr] === breakdown?._meta?.override_id &&
        order.middle_mile_shipper_rate_override_id === breakdown?._middle_mile?._meta?.override_id;
    const client = overridesById[breakdown?._meta?.override_id]?.assign || {};
    const mmClient = overridesById[breakdown?._middle_mile?._meta?.override_id]?.assign || {};

    const total = breakdown.total_charge ? dollar.format(breakdown.total_charge) : '--';

    const Carrier = ({ client, subtext, breakdown, isLast, price }) => {
        const label = client?.business_name || 'Onward';
        const logo = client?.profile_img || onwardLogo;
        const logoBg = client?.profile_img_color || colors.white.primary;

        let Icon, iconColor, iconTooltip;
        switch (overridesById[breakdown?._meta?.override_id]?.marketplace_tier) {
            case MARKETPLACE_TIERS.T1_INTEGRATIONS_ONLY:
                Icon = HandshakeIcon;
                iconColor = colors.golds.primary;
                iconTooltip = 'This is your partner carrier that is connected via Onward Integrations';
                break;
            case MARKETPLACE_TIERS.T2_INTEGRATIONS_ONLY:
            case MARKETPLACE_TIERS.T2_INTEGRATIONS_BILLING:
                Icon = CheckCircleIcon;
                iconColor = colors.greens.primary;
                iconTooltip = 'This carrier is available to you through the Onward Marketplace';
                break;
        }

        return (
            <Grid
                container
                direction="row"
                css={css`
                    margin-bottom: ${!isLast ? '16px' : '0px'};
                `}
            >
                <Grid
                    container
                    direction="column"
                    css={css`
                        flex: 0;
                        flex-basis: 0;
                        justify-content: center;
                        margin-right: 20px;
                    `}
                >
                    <Circle
                        css={css`
                            background: ${logoBg};

                            display: flex;
                            justify-content: center;
                            align-items: center;
                        `}
                    >
                        <img
                            css={css`
                                object-fit: contain;
                                display: flex;
                                height: 35px;
                                width: 35px;
                            `}
                            src={logo}
                        />
                    </Circle>
                </Grid>
                <Grid
                    container
                    direction="column"
                    css={css`
                        flex: 1;
                        flex-basis: 0;
                        justify-content: center;
                    `}
                >
                    <Grid
                        container
                        direction="row"
                        css={css`
                            align-items: center;
                        `}
                    >
                        <Label
                            css={css`
                                color: #2b2b2b;
                            `}
                        >
                            {label}
                        </Label>
                        {Icon ? (
                            <Tooltip placement="top" title={iconTooltip}>
                                <Icon
                                    css={css`
                                        margin-left: 12px;
                                        color: ${iconColor};
                                        width: 20px;
                                    `}
                                />
                            </Tooltip>
                        ) : null}
                    </Grid>
                    <Grid
                        container
                        direction="row"
                        css={css`
                            margin-bottom: 4px;
                        `}
                    >
                        <SubTitle
                            css={css`
                                color: #2b2b2b;
                            `}
                        >
                            {`${breakdown._meta.pu_location} - ${breakdown._meta.do_location}`}
                        </SubTitle>
                    </Grid>
                    <Grid container direction="row">
                        <SubTitle
                            css={css`
                                color: #2b2b2b;
                            `}
                        >
                            {subtext}
                        </SubTitle>
                    </Grid>
                </Grid>
                <Grid
                    container
                    direction="column"
                    css={css`
                        flex: 0;
                        flex-basis: 0;
                        justify-content: center;
                    `}
                >
                    <Label
                        css={css`
                            color: #979797;
                            white-space: nowrap;
                        `}
                    >
                        {`${price}`}
                    </Label>
                </Grid>
            </Grid>
        );
    };

    const onClick = () => {
        const patch = {
            [override_attr]: breakdown?._meta?.override_id,
            middle_mile_shipper_rate_override_id: breakdown?._middle_mile?._meta?.override_id,
        };
        const disclaimers = [breakdown, breakdown?._middle_mile]
            .map((breakdown) => genOptionDisclaimer({ order, breakdown }))
            .filter((x) => !!x);

        if (disclaimers.length > 0) {
            callbacks.setDisclaimer({
                order: {
                    ...order,
                    ...patch,
                },
                disclaimers,
            });
        } else {
            callbacks.submitOrder({
                ...order,
                ...patch,
            });
        }
    };

    return (
        <OptionRow selected={isSelected} className="option" {...rest} onClick={onClick}>
            <Grid container direction="row">
                <Grid
                    container
                    direction="column"
                    css={css`
                        flex: 0;
                        flex-basis: 0;
                        justify-content: center;
                        margin-right: 8px;
                    `}
                >
                    <Checkbox color="primary" checked={isSelected} disabled={isSelected} />
                </Grid>
                <Grid
                    container
                    direction="column"
                    css={css`
                        flex: 1;
                        flex-basis: 0;
                    `}
                >
                    {[
                        {
                            client: mmClient,
                            subtext: 'Middle Mile',
                            breakdown: breakdown?._middle_mile || {},
                            price: breakdown.middle_mile_charge ? dollar.format(breakdown.middle_mile_charge) : '--',
                        },
                        {
                            client,
                            subtext: 'Final Mile',
                            breakdown,
                            price: breakdown.shipper_charge ? dollar.format(breakdown.shipper_charge) : '--',
                        },
                    ].map((params, idx) => {
                        return <Carrier isLast={idx === 1} {...params} />;
                    })}
                </Grid>
            </Grid>
            <Grid container direction="row">
                <Grid
                    container
                    direction="column"
                    css={css`
                        margin-top: 4px;
                        align-items: flex-end;
                    `}
                >
                    <Label
                        css={css`
                            color: #2b2b2b;
                            white-space: nowrap;
                        `}
                    >
                        {`Price: ${total}`}
                    </Label>
                </Grid>
            </Grid>
        </OptionRow>
    );
};
